import {
  fetchFiles,
  fetchFolders,
  fetchItemsCount,
  fetchItemsWithFlow,
  fetchUsers,
  getItemsFromCollection,
  getNotificationsFromDirectus,
} from "directus/src/operations";
import { apiRoot } from "services/src/config";

export function getFetchFunctions(endpoint, flowId, custom = false) {
  if (custom) {
    return {
      fetchFn: async (col, query) => {
        const { data } = await apiRoot.get(`custom/items/${col}`, {
          params: {
            ...query,
            fields: query.fields
              ?.flatMap((f) =>
                typeof f === "object" && f
                  ? Object.entries(f).flatMap(([k, v]) => v.map((s) => `${k}.${s}`))
                  : f
              )
              .join(","),
            filter: typeof query.filter === "object" ? JSON.stringify(query.filter) : query.filter,
          },
        });

        return data;
      },
      // Custom API returns pagination in `meta`, so count is read from the
      // fetch response instead of making a separate aggregate request.
      countFromResponse: (res) => res?.meta?.filter_count ?? res?.meta?.total_count ?? 0,
      itemsFromResponse: (res) => res?.data || [],
      metaFromResponse: (res) => res?.meta || {},
    };
  }

  switch (endpoint) {
    case "notifications":
      return {
        fetchFn: async (_, query) => getNotificationsFromDirectus(query),
        countFn: async (_, query) =>
          getNotificationsFromDirectus({
            aggregate: { countDistinct: ["id"] },
            filter: query?.query?.filter || {},
          }),
      };
    case "users":
      return {
        fetchFn: async (_, query) => fetchUsers(query),
        countFn: async (_, query) =>
          fetchUsers({
            aggregate: { countDistinct: ["id"] },
            filter: query?.query?.filter || {},
            search: query?.query?.search || "",
          }),
      };
    case "files":
      return {
        fetchFn: async (_, query) => fetchFiles(query),
        countFn: async (_, query) =>
          fetchFiles({
            aggregate: { countDistinct: ["id"] },
            filter: query?.query?.filter || {},
          }),
      };
    case "folders":
      return {
        fetchFn: async (_, query) => fetchFolders(query),
        countFn: async (_, query) =>
          fetchFolders({
            aggregate: { countDistinct: ["id"] },
            filter: query?.query?.filter || {},
          }),
      };
    case "flow":
      return {
        fetchFn: async (_, query) => {
          return fetchItemsWithFlow(flowId, query.filter);
        },
      };
    default:
      return {
        fetchFn: getItemsFromCollection,
        countFn: fetchItemsCount,
      };
  }
}

/**
 * Fetches list items and count with a single normalized result shape,
 * regardless of whether the source is Directus SDK or the custom API.
 * @returns {Promise<{ items: any[], count: number, meta: Object, raw: any }>}
 */
export async function fetchListResult(endpoint, query, { flowId, custom = false, itemKey } = {}) {
  const { fetchFn, countFn, countFromResponse, itemsFromResponse, metaFromResponse } =
    getFetchFunctions(endpoint, flowId, custom);

  // Only hit the separate count endpoint when the response doesn't carry it
  const needsCountCall = countFn && !countFromResponse;

  const [res, countRes] = await Promise.all([
    fetchFn(endpoint, query),
    needsCountCall
      ? countFn(endpoint, {
          aggregate: { countDistinct: ["id"] },
          query: {
            ...(query.search ? { search: query.search } : {}),
            filter: query?.filter || {},
          },
        })
      : undefined,
  ]);

  const items = itemsFromResponse ? itemsFromResponse(res) : (itemKey && res?.[itemKey]) || res;
  const count = countFromResponse
    ? countFromResponse(res)
    : countRes?.[0]?.countDistinct?.id || 0;
  const meta = metaFromResponse ? metaFromResponse(res) : {};

  return { items, count, meta, raw: res };
}
